import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  X,
  MessageCircleMore,
  Paperclip,
  Send,
  Loader2,
  ShieldAlert,
  Download
} from 'lucide-react';
import { chatService } from '../../services/chat.service';
import { showAppAlert } from '../../utils/appAlert';

const HIDDEN_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/verify', '/account-verification'];

const formatAttachmentSize = (value) => {
  if (!value) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / (1024 * 102.4)) / 10} MB`;
};

const CustomerSupportChat = () => {
  const location = useLocation();
  const panelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState({});
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);

  const token = localStorage.getItem('token');
  const shouldRender = Boolean(token) && !HIDDEN_PATHS.includes(location.pathname);

  const unreadCount = conversation?.unreadCount || 0;

  const loadConversation = async (loadMessages = false) => {
    try {
      const conversationData = await chatService.getConversation();
      setConversation(conversationData);

      if (loadMessages) {
        const messageData = await chatService.getMessages();
        setMessages(messageData || []);
      }
    } catch (error) {
      // stay silent on passive refreshes
    }
  };

  useEffect(() => {
    if (!shouldRender) return undefined;

    loadConversation(false);
    const timer = window.setInterval(() => {
      if (isOpen) {
        loadConversation(true);
      } else {
        loadConversation(false);
      }
    }, isOpen ? 3000 : 5000);

    return () => window.clearInterval(timer);
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!isOpen || !shouldRender) return;

    const openChat = async () => {
      try {
        setLoading(true);
        const [conversationData, messageData] = await Promise.all([
          chatService.getConversation(),
          chatService.getMessages()
        ]);
        setConversation(conversationData);
        setMessages(messageData || []);
        await chatService.markRead();
        setConversation((current) => current ? { ...current, unreadCount: 0 } : conversationData);
      } catch (error) {
        showAppAlert({
          type: 'error',
          title: 'Chat Unavailable',
          message: error.message || 'Failed to load support chat.'
        });
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    };

    openChat();
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!panelRef.current) return;
    panelRef.current.scrollTop = panelRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    return () => {
      Object.values(attachmentPreviewUrls).forEach((url) => {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      });
    };
  }, [attachmentPreviewUrls]);

  useEffect(() => {
    const previewMessages = messages.filter(
      (message) => message.hasAttachment && message.attachmentContentType?.startsWith('image/')
    );

    let isCancelled = false;

    const loadPreviews = async () => {
      for (const message of previewMessages) {
        if (attachmentPreviewUrls[message.id]) {
          continue;
        }

        try {
          const blobUrl = await chatService.getAttachmentBlobUrl(message.id);
          if (isCancelled) {
            window.URL.revokeObjectURL(blobUrl);
            return;
          }
          setAttachmentPreviewUrls((current) => ({ ...current, [message.id]: blobUrl }));
        } catch (error) {
          // ignore preview failures and keep download option available
        }
      }
    };

    loadPreviews();

    return () => {
      isCancelled = true;
    };
  }, [attachmentPreviewUrls, messages]);

  const canSend = useMemo(() => content.trim().length > 0 || Boolean(file), [content, file]);

  const handleSend = async (event) => {
    event.preventDefault();
    if (!canSend) return;

    try {
      setSending(true);
      await chatService.sendMessage({ content, file });
      const [conversationData, messageData] = await Promise.all([
        chatService.getConversation(),
        chatService.getMessages()
      ]);
      setConversation(conversationData);
      setMessages(messageData || []);
      setContent('');
      setFile(null);
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Send Failed',
        message: error.message || 'Failed to send support message.'
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async (message) => {
    try {
      await chatService.downloadAttachment(message.id, message.attachmentName);
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Download Failed',
        message: error.message || 'Failed to download attachment.'
      });
    }
  };

  const handleAttachmentChange = (event) => {
    setFile(event.target.files?.[0] || null);
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="fixed bottom-5 right-4 sm:right-5 z-[160]">
      {isOpen ? (
        <div className="w-[min(94vw,380px)] h-[min(78vh,620px)] max-h-[calc(100vh-40px)] rounded-[2rem] border border-gray-200 bg-white shadow-2xl shadow-black/15 overflow-hidden flex flex-col">
          <div className="shrink-0 px-5 py-4 bg-brand-black text-white flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-2">Live Support</p>
              <h3 className="text-xl font-black tracking-tight">Chat with Go Rides</h3>
              <p className="text-xs text-gray-300 mt-1">
                {conversation?.assignedAdminName ? `Connected to ${conversation.assignedAdminName}` : 'A support admin will reply here.'}
              </p>
            </div>
            <button type="button" onClick={() => setIsOpen(false)} className="text-gray-300 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div ref={panelRef} className="flex-1 min-h-0 overflow-y-auto bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] px-4 py-5 space-y-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center px-6">
                    <ShieldAlert className="w-10 h-10 text-gray-300 mb-4" />
                    <h4 className="text-lg font-black tracking-tight mb-2">Start a support conversation</h4>
                    <p className="text-sm text-gray-500 leading-6">
                      Ask about bookings, refunds, payments, or account issues. You can also attach supporting documents.
                    </p>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isCustomer = message.senderType === 'CUSTOMER';
                    return (
                      <div key={message.id} className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[82%] rounded-[1.4rem] px-4 py-3 shadow-sm ${isCustomer ? 'bg-brand-black text-white' : 'bg-white text-brand-black border border-gray-200'}`}>
                          {message.content ? (
                            <p className="text-sm leading-6 whitespace-pre-wrap">{message.content}</p>
                          ) : null}
                          {message.hasAttachment && message.attachmentContentType?.startsWith('image/') && attachmentPreviewUrls[message.id] ? (
                            <button
                              type="button"
                              onClick={() => handleDownload(message)}
                              className="mt-3 block"
                            >
                              <img
                                src={attachmentPreviewUrls[message.id]}
                                alt={message.attachmentName || 'Attachment preview'}
                                className="w-full max-h-56 object-cover rounded-2xl border border-black/10"
                              />
                            </button>
                          ) : null}
                          {message.hasAttachment ? (
                            <button
                              type="button"
                              onClick={() => handleDownload(message)}
                              className={`mt-3 inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-black uppercase tracking-[0.14em] ${isCustomer ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}`}
                            >
                              <Download className="w-4 h-4" />
                              {message.attachmentContentType?.startsWith('image/') && attachmentPreviewUrls[message.id]
                                ? 'Download Image'
                                : (message.attachmentName || 'Attachment')}
                              {message.attachmentSize ? `(${formatAttachmentSize(message.attachmentSize)})` : ''}
                            </button>
                          ) : null}
                          <p className={`mt-3 text-[11px] font-bold ${isCustomer ? 'text-gray-300' : 'text-gray-400'}`}>
                            {message.senderName} • {message.sentAt}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <form onSubmit={handleSend} className="shrink-0 px-4 py-3 border-t border-gray-200 bg-white">
                {file ? (
                  <div className="mb-3 inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-700">
                    <Paperclip className="w-4 h-4" />
                    <span className="max-w-[220px] truncate">{file.name}</span>
                    <button type="button" onClick={() => setFile(null)} className="text-slate-500 hover:text-slate-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : null}
                <div className="flex items-end gap-3">
                  <div className="relative flex-1">
                    <label className="absolute left-3 bottom-3 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-slate-100 text-slate-600 cursor-pointer hover:bg-slate-200 transition-colors">
                      <Paperclip className="w-4 h-4" />
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                        onChange={handleAttachmentChange}
                        className="hidden"
                      />
                    </label>
                  <textarea
                    rows={2}
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                    placeholder="Type your message..."
                    className="w-full min-h-[72px] rounded-2xl border border-gray-200 pl-14 pr-4 py-3 text-sm font-medium outline-none resize-none focus:border-brand-black"
                  />
                  </div>
                  <button
                    type="submit"
                    disabled={!canSend || sending}
                    className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-brand-black text-white disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="relative inline-flex items-center gap-3 rounded-full bg-brand-black text-white px-5 py-4 shadow-2xl shadow-black/20 hover:bg-black transition-colors"
        >
          <MessageCircleMore className="w-5 h-5" />
          <span className="text-sm font-black uppercase tracking-[0.14em]">Live Chat</span>
          {unreadCount > 0 ? (
            <span className="absolute -top-2 -right-1 min-w-[24px] h-6 px-2 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center">
              {unreadCount}
            </span>
          ) : null}
        </button>
      )}
    </div>
  );
};

export default CustomerSupportChat;
